import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useCallback } from "react"

import useScrollShadow from "./useScrollShadow"

const SHADOW_CONFIG = {
  horizontal: {
    start: {
      className: "w-3 bg-gradient-to-r from-black/10 to-transparent",
      style: { top: 0, bottom: 0, left: 0 },
    },
    end: {
      className: "w-3 bg-gradient-to-l from-black/10 to-transparent",
      style: { top: 0, bottom: 0, right: 0 },
    },
  },
  vertical: {
    start: {
      className: "h-3 bg-gradient-to-b from-black/10 to-transparent",
      style: { top: 0, left: 0, right: 0 },
    },
    end: {
      className: "h-3 bg-gradient-to-t from-black/10 to-transparent",
      style: { bottom: 0, left: 0, right: 0 },
    },
  },
}

const ScrollShadow = ({
  direction = "vertical",
  className,
  scrollContainerClassName,
  scrollContainerRef: externalRef,
  zIndex = 10,
  startShadowClassName,
  endShadowClassName,
  startShadowStyle,
  endShadowStyle,
  onScroll,
  children,
}) => {
  const {
    scrollContainerRef: internalRef,
    startShadowRef,
    endShadowRef,
  } = useScrollShadow({ direction })

  const mergedRef = useCallback(
    (node) => {
      internalRef.current = node
      if (typeof externalRef === "function") {
        externalRef(node)
      } else if (externalRef) {
        externalRef.current = node
      }
    },
    [externalRef, internalRef]
  )

  const config = SHADOW_CONFIG[direction]

  return (
    <div className={classNames("relative", className)}>
      <div
        ref={startShadowRef}
        className={classNames(
          "pointer-events-none absolute transition-opacity duration-150",
          config.start.className,
          startShadowClassName
        )}
        style={{
          opacity: 0,
          zIndex,
          ...config.start.style,
          ...startShadowStyle,
        }}
      />
      <div
        ref={endShadowRef}
        className={classNames(
          "pointer-events-none absolute transition-opacity duration-150",
          config.end.className,
          endShadowClassName
        )}
        style={{ opacity: 0, zIndex, ...config.end.style, ...endShadowStyle }}
      />
      <div
        ref={mergedRef}
        className={classNames("overflow-auto", scrollContainerClassName)}
        onScroll={onScroll}
      >
        {children}
      </div>
    </div>
  )
}

ScrollShadow.propTypes = {
  direction: PropTypes.oneOf(["horizontal", "vertical"]),
  className: PropTypes.string,
  scrollContainerClassName: PropTypes.string,
  scrollContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  zIndex: PropTypes.number,
  startShadowClassName: PropTypes.string,
  endShadowClassName: PropTypes.string,
  startShadowStyle: PropTypes.object,
  endShadowStyle: PropTypes.object,
  onScroll: PropTypes.func,
  children: PropTypes.node,
}

export default ScrollShadow
